import { useMutation } from '@tanstack/react-query';
import { Alert } from '../atoms/Alert/Alert';
import { Button } from '../atoms/Button/Button';
import { Card } from '../atoms/Card/Card';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { Heading } from '../atoms/Typography/Heading';
import { Text } from '../atoms/Typography/Text';
import { AiLimitErrorMessage } from '../aiUsage/AiLimitNotice';
import { getAiLimitErrorKind } from '../../services/aiUsageService';
import { generateAquariumStory } from '../../services/journalAiService';
import { useTranslation } from '../../i18n/useTranslation';
import { formatGeneratedAt } from './formatGeneratedAt';
import './JournalAi.css';

/**
 * "Aquarium story" on the journal page: AI writes a short story of the tank
 * from the latest journal entries — only when the user asks (one metered AI
 * call per click), in the UI language. Not stored.
 */
export function AquariumStoryCard({ aquariumId }: { aquariumId: string }) {
  const { t, i18n } = useTranslation();
  const mutation = useMutation({ mutationFn: () => generateAquariumStory(aquariumId) });
  const story = mutation.data;
  const limitError = mutation.error != null && getAiLimitErrorKind(mutation.error) !== null;

  return (
    <Card className="rg-journal-ai rg-journal-ai--story" data-testid="aquarium-story-card">
      <Heading level={2} variant="card">{t('journal.story.title')}</Heading>
      <Text color="secondary">{t('journal.story.description')}</Text>
      <div className="rg-journal-ai__actions">
        <Button
          type="button"
          variant="secondary"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
          isLoading={mutation.isPending}
        >
          {mutation.isPending ? t('journal.story.generating') : story ? t('journal.story.regenerate') : t('journal.story.generate')}
        </Button>
      </div>
      <LiveRegion visuallyHidden busy={mutation.isPending}>
        {mutation.isPending ? t('journal.story.generating') : story ? t('journal.story.done') : ''}
      </LiveRegion>
      {mutation.isError && (limitError
        ? <AiLimitErrorMessage error={mutation.error} />
        : <Alert tone="danger">{t('journal.story.failed')}</Alert>)}
      {story && (story.entryCount === 0 ? (
        <Text data-testid="aquarium-story-empty">{t('journal.story.empty')}</Text>
      ) : (
        <div className="rg-journal-ai__result" data-testid="aquarium-story-text">
          <Text className="rg-journal-ai__story">{story.story}</Text>
          <Text size="sm" color="muted">
            {t('journal.ai.generatedAt', { date: formatGeneratedAt(story.generatedAt, i18n.language) })}
            {' · '}
            {t('journal.ai.disclaimer')}
          </Text>
        </div>
      ))}
    </Card>
  );
}

export default AquariumStoryCard;
